/**
 * The team (agents first, train 1b): the person's roster, Bash first, and the
 * ONE app-wide selection the pill follows. `selectedHandle: null` is the lead.
 * Session-only, never persisted: a reload starts on Bash, which is what every
 * reload did before agents existed.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { LEAD_HANDLE, type TeamAgent } from "@bash/shared";

interface TeamState {
  agents: TeamAgent[];
  loaded: boolean;
  selectedHandle: string | null;
  setAgents: (agents: TeamAgent[]) => void;
  select: (handle: string | null) => void;
  upsert: (agent: TeamAgent) => void;
  remove: (handle: string) => void;
}

export const useTeamStore = create<TeamState>()(
  devtools(
    (set) => ({
      agents: [],
      loaded: false,
      selectedHandle: null,
      setAgents: (agents) =>
        set((s) => ({
          agents,
          loaded: true,
          // A pick whose agent is no longer on the roster reads as the lead.
          selectedHandle:
            s.selectedHandle &&
            agents.some((a) => a.handle === s.selectedHandle)
              ? s.selectedHandle
              : null,
        })),
      select: (handle) =>
        set({
          selectedHandle: handle && handle !== LEAD_HANDLE ? handle : null,
        }),
      upsert: (agent) =>
        set((s) => {
          const i = s.agents.findIndex((a) => a.handle === agent.handle);
          if (i === -1) return { agents: [...s.agents, agent] };
          const next = s.agents.slice();
          next[i] = agent;
          return { agents: next };
        }),
      remove: (handle) =>
        set((s) => ({
          agents: s.agents.filter((a) => a.handle !== handle),
          selectedHandle: s.selectedHandle === handle ? null : s.selectedHandle,
        })),
    }),
    { name: "team" }
  )
);

/** The agent the pill speaks to; null is the lead. */
export const selectSelectedAgent = (s: TeamState): TeamAgent | null =>
  s.selectedHandle
    ? (s.agents.find(
        (a) => a.handle === s.selectedHandle && a.kind === "agent"
      ) ?? null)
    : null;
